import { Link } from '../router';
import {
  availabilityLabel,
  availabilityTone,
  platformCapabilities,
  principles,
  products,
} from '../content';
import { endpoints } from '../config';

/**
 * §4.1: value proposition, suite overview, availability, benefits and a call to action.
 *
 * The availability of each application is stated on its own card rather than buried, and it is
 * read from the same list the products page uses — so a product that ships is announced here by
 * changing one word in one file, rather than by somebody remembering this page exists.
 *
 * Three are buyable today. The hero says which, because a page that implies more sets up a
 * disappointing first conversation and one that implies fewer loses the call before it starts.
 */
export default function Home({ navigate }: { navigate: (to: string) => void }) {
  return (
    <>
      <section className="hero">
        <div className="wrap hero__inner">
          <span
            className="pill pill--now"
            style={{ marginBottom: 18, background: 'rgba(255,255,255,0.16)', color: '#fff' }}
          >
            CRM, SRM and Inventory available now
          </span>

          <h1>One account. Every application your organisation needs.</h1>

          <p className="hero__lede">
            ClientMa is a multi-tenant business platform from SJS Tech. Sell with CRM, buy with
            SRM, and know what you hold with Inventory — each its own subscription, and none of
            them needing the others. Add HR when it arrives, without a second login, a second
            tenant, or a second copy of your data.
          </p>

          <div className="hero__actions">
            <Link to="/signup" onNavigate={navigate} className="btn btn--primary btn--lg">
              Start a free trial
            </Link>
            <Link to="/products" onNavigate={navigate} className="btn btn--onDark btn--lg">
              See the applications
            </Link>
          </div>

          <p className="hero__note">
            No card required to trial. Country-aware pricing in your own currency.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <div className="section__head">
            <span className="eyebrow">The suite</span>
            <h2>Subscribe to what you use, not to a bundle you don’t</h2>
            <p>
              Each application is its own subscription with its own seats. One tenant holds them
              all, so a contact created in CRM is the same contact everywhere else.
            </p>
          </div>

          <div className="grid grid--3">
            {products.map((product) => (
              <article key={product.key} className="card">
                {availabilityLabel(product.availability) && (
                  <span className={`pill pill--${availabilityTone(product.availability)}`}>
                    {availabilityLabel(product.availability)}
                  </span>
                )}

                <h3 style={{ marginTop: availabilityLabel(product.availability) ? 14 : 0 }}>
                  {product.name}
                </h3>
                <p>{product.summary}</p>
              </article>
            ))}
          </div>

          <p style={{ marginTop: 28 }}>
            <Link to="/products" onNavigate={navigate}>
              What each application does →
            </Link>
          </p>
        </div>
      </section>

      <section className="section section--sunken">
        <div className="wrap">
          <div className="section__head">
            <span className="eyebrow">How it is built</span>
            <h2>The things you only notice when they are missing</h2>
            <p>
              Every application inherits the same identity, security, billing and audit. Nothing
              below is a feature you buy separately.
            </p>
          </div>

          <div className="grid grid--3">
            {principles.map((principle) => (
              <article key={principle.title} className="card card--flat">
                <h3>{principle.title}</h3>
                <p>{principle.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <div className="grid grid--2" style={{ alignItems: 'start', gap: 48 }}>
            <div>
              <span className="eyebrow">Shared platform</span>
              <h2>Built once, underneath everything</h2>
              <p style={{ color: 'var(--muted)' }}>
                A tenant is an organisation, not a database copy. Companies, branches, departments
                and teams sit inside it, and what a person can see follows from where they sit —
                not from which application they happen to be in.
              </p>

              <p style={{ marginTop: 22 }}>
                <a href={endpoints.api}>See the API reference →</a>
              </p>
            </div>

            <div className="grid" style={{ gap: 16 }}>
              {platformCapabilities.map((capability) => (
                <article key={capability.title} className="card card--flat">
                  <h3>{capability.title}</h3>
                  <p>{capability.body}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section section--sunken">
        <div className="wrap">
          <div className="band">
            <h2>Start this week</h2>
            <p>
              Register an organisation, verify your email, pick the application you need and
              invite your team. Add another later and it joins the same tenant — same people,
              same permissions, nothing to migrate.
            </p>
            <div className="band__actions">
              <Link to="/signup" onNavigate={navigate} className="btn btn--onDark btn--lg">
                Create an account
              </Link>
              <Link to="/company" onNavigate={navigate} className="btn btn--onDark btn--lg">
                Talk to us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
